import React, { useState } from 'react'
import { Box, Collapse, type SxProps, type Theme, Tooltip } from '@mui/material'

import AILoadingAnimation from '~assets/AILoadingAnimation'
import { Icon } from '~components/icons'
import { IconButton, Typography } from '~components/internal'
import { type StreamStatusPhase } from '~hooks/useStreamAIResponse'
import { lightTokens, typography } from '~tokens/internal'
import {
  type ChatStreamStatusStats,
  type ChatStreamStepEntry,
  type ChatStreamStepItem,
  type EnrichedOrganizationAISettings,
} from '~types'

interface StepDisplay {
  icon: string
  label: string
}

// Synthetic step (not sent by the backend); ChatView merges retrieval completions into one of these
export const RETRIEVAL_SUMMARY_STEP = 'retrieval_summary'

const stepDisplays: Record<string, StepDisplay> = {
  retrieve_sources: { icon: 'travel_explore', label: 'Searching' },
  expand_page_source: { icon: 'article', label: 'Reading found pages' },
  search_page_sources: { icon: 'find_in_page', label: 'Searching pages' },
  search_entities: { icon: 'hub', label: 'Searching entities' },
  search_contacts: { icon: 'contact_page', label: 'Searching contacts' },
  search_cases: { icon: 'folder_open', label: 'Searching cases' },
  gis_lookup: { icon: 'pin_drop', label: 'Looking for a location' },
  external_api_call: { icon: 'api', label: 'Checking external service' },
  generate_document: { icon: 'description', label: 'Generating document' },
  create_skill: { icon: 'lightbulb', label: 'Creating skill' },
  update_skill: { icon: 'lightbulb', label: 'Updating skill' },
  citation: { icon: 'format_quote', label: 'Citing sources' },
  generation: { icon: 'edit_note', label: 'Drafting answer' },
  [RETRIEVAL_SUMMARY_STEP]: { icon: 'travel_explore', label: 'Searching' },
}

const fallbackStepDisplay: StepDisplay = { icon: 'search', label: 'Thinking' }

// Completed labels for the non-search steps
const neutralCompletedLabels: Record<string, string> = {
  generation: 'Drafted answer',
  generate_document: 'Generated document',
  create_skill: 'Created skill',
  update_skill: 'Updated skill',
}
const STEP_LABEL_MAX_CHARS = 72

const stepTextColor = lightTokens.text.secondary
const stepFontSize = typography.fontSizePx.xs
const stepIconSize = 18
// Every row (loading animation, check, step icons) shares this slot so labels line up
const iconSlotSize = 24

const StepIcon: React.FC<{ name: string; color: string }> = ({ name, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: iconSlotSize,
      height: iconSlotSize,
      flexShrink: 0,
    }}
  >
    <Icon name={name} fill={0} size={stepIconSize} color={color} />
  </Box>
)

export const getStepDisplay = (step: string): StepDisplay =>
  stepDisplays[step] ?? fallbackStepDisplay

// Steps that represent actual work worth showing in the step list
export const isDisplayableStreamStep = (step: string): boolean => step in stepDisplays

export const normalizeStreamStep = (item: ChatStreamStepItem): ChatStreamStepEntry =>
  typeof item === 'string' ? { step: item } : item

// Tools whose completions get merged into RETRIEVAL_SUMMARY_STEP instead of shown individually
const retrievalSteps = new Set([
  'web_search',
  'retrieve_sources',
  'vector_search',
  'text_search',
  'expand_page_source',
])

export const isRetrievalStep = (step: string): boolean => retrievalSteps.has(step)

const plural = (count: number, singular: string, pluralForm?: string): string =>
  count === 1 ? singular : (pluralForm ?? `${singular}s`)

export const getStartedLabel = (step: string, stats?: ChatStreamStatusStats): string => {
  const base = getStepDisplay(step).label
  const query = stats?.query
  switch (step) {
    case 'web_search':
      return query ? `Searching the web for '${query}'` : base
    case 'retrieve_sources':
    case 'vector_search':
    case 'text_search':
      return query ? `Searching for '${query}'` : base
    case 'search_page_sources':
      return query ? `Searching pages matching '${query}'` : base
    case 'search_entities': {
      const entityType = stats?.entityType || 'entities'
      return query ? `Searching ${entityType} for '${query}'` : base
    }
    case 'search_contacts':
      return query ? `Searching contacts for '${query}'` : base
    case 'search_cases':
      return query ? `Searching cases for '${query}'` : base
    case 'gis_lookup':
      return query ? `Looking for a location matching ${query}` : base
    case 'external_api_call':
      return stats?.name ? `Checking ${stats.name}` : base
    case 'create_skill':
      return stats?.name ? `Creating skill '${stats.name}'` : base
    case 'update_skill':
      return stats?.name ? `Updating skill '${stats.name}'` : base
    default:
      return base
  }
}

const getCompleteLabel = (step: string, stats: ChatStreamStatusStats): string | null => {
  if (step === RETRIEVAL_SUMMARY_STEP && stats.count !== undefined) {
    return `Found ${stats.count} ${plural(stats.count, 'source')} so far`
  }
  switch (step) {
    case 'search_page_sources':
      return stats.totalFound !== undefined
        ? `Found ${stats.totalFound} matching ${plural(stats.totalFound, 'page')}`
        : null
    case 'search_entities': {
      const entityType = stats.entityType || 'entities'
      const total = stats.total ?? (stats.firstName ? 1 : 0)
      if (total === 0) return `No matching ${entityType} found`
      if (!stats.firstName) return `Found ${total} matching ${entityType}`
      const others = total - 1
      return others > 0
        ? `Found '${stats.firstName}' and ${others} ${plural(others, 'other')}`
        : `Found '${stats.firstName}'`
    }
    case 'search_contacts':
      return stats.name ? `Found contact '${stats.name}'` : 'No matching contact found'
    case 'search_cases':
      return stats.count !== undefined
        ? `Found ${stats.count} matching ${plural(stats.count, 'case')}`
        : null
    case 'gis_lookup':
      if (!stats.count) return 'No results found'
      return stats.layerName
        ? `Found ${stats.count} ${plural(stats.count, 'result')} in ${stats.layerName}`
        : `Found ${stats.count} ${plural(stats.count, 'result')}`
    case 'external_api_call': {
      const endpoint = stats.endpoint || 'external service'
      return stats.success ? `Retrieved data from ${endpoint}` : `Failed to reach ${endpoint}`
    }
    case 'citation':
      return stats.count !== undefined
        ? `Cited ${stats.count} ${plural(stats.count, 'source')}`
        : null
    default:
      return null
  }
}

// Shared reducer behind useChatStreamSteps, used by every chat/search/assistant surface
export const accumulateStreamStep = (
  steps: ChatStreamStepEntry[],
  response: {
    step: string
    status_phase?: StreamStatusPhase
    status_stats?: ChatStreamStatusStats
  },
): ChatStreamStepEntry[] => {
  if (!isDisplayableStreamStep(response.step)) return steps

  if (response.status_phase === 'complete') {
    if (isRetrievalStep(response.step)) {
      // Keep a running total for each retrieval agent turn
      const lastSummaryIndex = steps.findLastIndex((entry) => entry.step === RETRIEVAL_SUMMARY_STEP)
      const newTurnStartedSinceLastSummary = steps
        .slice(lastSummaryIndex + 1)
        .some((entry) => isRetrievalStep(entry.step) && entry.startedStats)
      const startNewSummary = lastSummaryIndex < 0 || newTurnStartedSinceLastSummary
      const priorCount =
        lastSummaryIndex >= 0 ? (steps[lastSummaryIndex].completeStats?.count ?? 0) : 0
      const newCount = priorCount + (response.status_stats?.count ?? 0)
      if (!startNewSummary) {
        const updated = [...steps]
        updated[lastSummaryIndex] = {
          ...updated[lastSummaryIndex],
          completeStats: { count: newCount },
        }
        return updated
      }
      return [...steps, { step: RETRIEVAL_SUMMARY_STEP, completeStats: { count: newCount } }]
    }
    // Attach complete stats to the most recent unfinished entry for this step
    const targetIndex = steps.findLastIndex(
      (entry) => entry.step === response.step && !entry.completeStats,
    )
    if (targetIndex >= 0) {
      const updated = [...steps]
      updated[targetIndex] = {
        ...updated[targetIndex],
        completeStats: response.status_stats ?? {},
      }
      return updated
    }
    return [...steps, { step: response.step, completeStats: response.status_stats ?? {} }]
  }

  const last = steps[steps.length - 1]
  const query = response.status_stats?.query
  // Same query already shown for this step (e.g. a repeated retrieval search)
  const isDuplicateQuery = Boolean(
    query &&
    steps.some((entry) => entry.step === response.step && entry.startedStats?.query === query),
  )
  // Skip consecutive duplicates only when neither event carries stats
  if (
    isDuplicateQuery ||
    (last && last.step === response.step && !response.status_stats && !last.startedStats)
  ) {
    return steps
  }
  return [...steps, { step: response.step, startedStats: response.status_stats }]
}

const getStepLabel = (entry: ChatStreamStepEntry): string => {
  if (entry.completeStats) {
    const completeLabel = getCompleteLabel(entry.step, entry.completeStats)
    if (completeLabel) return completeLabel
  }
  return getStartedLabel(entry.step, entry.startedStats)
}

interface ChatStreamStepsProps {
  steps: ChatStreamStepItem[]
  isStreaming?: boolean
  currentStep?: string
  orgAiSettings: EnrichedOrganizationAISettings
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  sx?: SxProps<Theme>
}

export const ChatStreamSteps: React.FC<ChatStreamStepsProps> = ({
  steps,
  isStreaming = false,
  currentStep,
  orgAiSettings,
  expanded: controlledExpanded,
  onExpandedChange,
  sx,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false)
  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : internalExpanded
  const toggleExpanded = (): void => {
    if (isControlled) onExpandedChange?.(!expanded)
    else setInternalExpanded((prev) => !prev)
  }

  if (!isStreaming && steps.length === 0) return null

  const entries = steps.map(normalizeStreamStep)
  const fontFamily = orgAiSettings.font_family || 'inherit'

  const retrievalSummaryEntries = entries.filter((entry) => entry.step === RETRIEVAL_SUMMARY_STEP)
  // The in-flight label should reflect the most recent retrieval turn's running total
  const retrievalSummaryEntry = retrievalSummaryEntries[retrievalSummaryEntries.length - 1]

  // A step sent that don't have a message for shouldn't blank out the real in-flight step still shown in entries
  const currentStepKnown = Boolean(currentStep && isDisplayableStreamStep(currentStep))
  const effectiveCurrentStep = currentStepKnown ? currentStep : entries[entries.length - 1]?.step

  // Prefer the retrieval summary over the in-flight entry's started label, or the header gets stuck on "Searching"
  const currentEntry = effectiveCurrentStep
    ? [...entries].reverse().find((entry) => entry.step === effectiveCurrentStep)
    : undefined
  const currentCompleteLabel =
    effectiveCurrentStep &&
    isRetrievalStep(effectiveCurrentStep) &&
    retrievalSummaryEntry?.completeStats
      ? getCompleteLabel(RETRIEVAL_SUMMARY_STEP, retrievalSummaryEntry.completeStats)
      : currentEntry?.completeStats
        ? getCompleteLabel(effectiveCurrentStep as string, currentEntry.completeStats)
        : null
  const currentLabel = effectiveCurrentStep
    ? (currentCompleteLabel ?? getStartedLabel(effectiveCurrentStep, currentEntry?.startedStats))
    : 'Thinking'

  const retrievedCount = retrievalSummaryEntry?.completeStats?.count
  const hasSearchActivity = entries.some((entry) => !(entry.step in neutralCompletedLabels))
  const completedHeader = hasSearchActivity
    ? retrievedCount !== undefined
      ? `Searched ${retrievedCount} ${plural(retrievedCount, 'source')}`
      : 'Searched sources'
    : (neutralCompletedLabels[entries[entries.length - 1]?.step] ?? 'Completed')

  const stepTextSx = { fontFamily, fontSize: stepFontSize, color: stepTextColor, minWidth: 0 }

  return (
    <Box
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          px: 2,
          py: 1,
          alignSelf: 'flex-start',
          maxWidth: '85%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {isStreaming ? (
          <AILoadingAnimation
            height={iconSlotSize}
            width={iconSlotSize}
            minHeight={0}
            orgColor={orgAiSettings.bg_color ? `#${orgAiSettings.bg_color}` : undefined}
            orgColorSecondary={
              orgAiSettings.link_color ? `#${orgAiSettings.link_color}` : undefined
            }
            statusText={currentLabel}
            fontSize={stepFontSize}
            fontFamily={fontFamily}
            color={stepTextColor}
            alignLeft
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <StepIcon name='check' color={lightTokens.success.main} />
            <Typography variant='body2' noWrap sx={stepTextSx}>
              {completedHeader}
            </Typography>
          </Box>
        )}
        {steps.length > 0 && (
          <Tooltip title={expanded ? 'Collapse steps' : 'Expand steps'}>
            <IconButton
              size='small'
              onClick={toggleExpanded}
              aria-label={expanded ? 'Collapse steps' : 'Expand steps'}
              sx={{ p: 0.5 }}
            >
              <Icon
                name='expand_more'
                size={20}
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {expanded && steps.length > 0 && (
        <Collapse in>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {entries.map((entry, index) => {
              const { icon } = getStepDisplay(entry.step)
              return (
                <Box
                  key={`${entry.step}-${index}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
                >
                  <StepIcon name={icon} color={stepTextColor} />
                  <Typography
                    variant='body2'
                    truncate
                    maxChars={STEP_LABEL_MAX_CHARS}
                    noWrap
                    sx={stepTextSx}
                  >
                    {getStepLabel(entry)}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  )
}

export default ChatStreamSteps
